import { openComponentInPopup, getComponentPart, getComponentById, QueryParameter, getComponent } from "./common";
import { PopupWidth, PopupOptions } from "./popup";
import ProductPrintingOptions from "./product-printing-options";
import PrintingDemand from "./printing-demand";
import OrderMatrix from "./order-matrix";
import ProductCount from "./product-count";
import Cart from "./cart";
import QuantityEdit from "./quantity-edit";
import LoadingIndication from "./loading-indication";

export default class PrintingOnlineDemand {
    private baseElement: HTMLElement;
    private linkElement: HTMLElement;
    private quantityEdit: QuantityEdit;

    private productId: number;
    private productGuid: string;

    private static readonly componentPartName = 'PrintingOnlineDemand';
    private static readonly onlineDemandButtonPartName = 'OnlineDemandButton';
    private static readonly quantityEditId = 'online-print-demand-count';
    private static readonly dialogId = 'onlinePrintingDemandPopup';
    private static readonly itemsLoadedAttrName = 'data-items-loaded';

    constructor(parentElement: HTMLElement) {
        this.baseElement = getComponentPart(parentElement, PrintingOnlineDemand.componentPartName);
        this.linkElement = getComponentPart(parentElement, PrintingOnlineDemand.onlineDemandButtonPartName);
        if (this.baseElement && this.linkElement)
            this.init();
    }

    private init() {
        const quantityEditElement = getComponentById(this.baseElement, PrintingOnlineDemand.quantityEditId);
        if (quantityEditElement) {
            this.quantityEdit = new QuantityEdit(quantityEditElement);
            this.productId = Number(this.baseElement.dataset.productId);
            this.productGuid = this.baseElement.dataset.productGuid;
        }

        this.linkElement.addEventListener('click', (evt: Event) => this.demandButtonClick(evt));
    }

    private async demandButtonClick(evt: Event): Promise<void> {
        evt.preventDefault();
        if (PrintingOnlineDemand.isPrintingAvailable()) {
            await this.openPrintingOptionsPopup();
        } else {
            await this.openPrintingDemandPopup();
        }
    }

    private static isPrintingAvailable(): boolean {
        const printingOptions = getComponent(document.body, ProductPrintingOptions.componentName);
        return printingOptions && printingOptions.hasAttribute(PrintingOnlineDemand.itemsLoadedAttrName);
    }

    private async openPrintingOptionsPopup(): Promise<void> {
        await openComponentInPopup(ProductPrintingOptions.componentName, PrintingOnlineDemand.getPopupParameters(), { id: PrintingOnlineDemand.dialogId } as PopupOptions, true);
        const optionsElement = getComponent(PrintingOnlineDemand.getDialogElement(), ProductPrintingOptions.componentName);
        const printingOptions = new ProductPrintingOptions(optionsElement);
        const productQuantity = this.getQuantity();
        if (productQuantity) {
            printingOptions.setProductCount(productQuantity);
        }
        printingOptions.initPrintingOptionLinks();
    }

    private static getPopupParameters(): QueryParameter[] {
        return [{ key: 'ajax', value: 'true' }];
    }

    private async openPrintingDemandPopup(): Promise<void> {
        await openComponentInPopup(PrintingDemand.componentName, PrintingOnlineDemand.getPopupParameters(), { id: PrintingOnlineDemand.dialogId, width: PopupWidth.Small } as PopupOptions, true);
        new PrintingDemand(PrintingOnlineDemand.getDialogElement(), (demandText) => this.processDemand(demandText));
    }

    private static getDialogElement(): HTMLElement {
        return document.querySelector(`#${PrintingOnlineDemand.dialogId}`) as HTMLElement;
    }

    private async processDemand(demandText: string) {
        const productQuantity = this.getQuantity();
        if (productQuantity) {
            await PrintingOnlineDemand.addSingleProductPrintingGroup(new ProductCount(this.productId, this.productGuid, productQuantity), demandText);
        } 
        else {
            await this.openOrderMatrix(demandText);
        }
    }

    private getQuantity(): number{
        return this.quantityEdit?.getValue();
    }

    private async openOrderMatrix(demandText: string): Promise<void> {
        await openComponentInPopup(OrderMatrix.componentName, [
            { key: 'printDescription', value: encodeURIComponent(demandText) }
        ], { id: 'order-matrix-popup' } as PopupOptions, true);

        new OrderMatrix(document.querySelector('#order-matrix-popup'), (counts) => {
            PrintingOnlineDemand.productCountSelected(counts, demandText);
        }, null);
    }

    private static async productCountSelected(counts: ProductCount[], demandText: string) {
        const cart = new Cart();
        await cart.addPrintingGroup(counts, demandText);
        await cart.openCartDialog(counts);
    }

    private static async addSingleProductPrintingGroup(count: ProductCount, demandText: string){
        const cart = new Cart();
        this.showLoadingIndication();
        await cart.addSingleProductPrintingGroup(count, demandText, null, null);
        this.hideLoadingIndication();
        await cart.openCartDialog([count]);
    }

    private static showLoadingIndication(): void {
        LoadingIndication.show(getComponent(document.body, PrintingOnlineDemand.componentPartName));
    }

    private static hideLoadingIndication(): void {
        LoadingIndication.hide(getComponent(document.body, PrintingOnlineDemand.componentPartName));
    }
}
